<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useClientStore } from '@/stores/client'
import { type DriverConfig, type DriverConfigsParams, driverConfigsApi } from '@/api/entities/review/driverConfigs'
import { type NaverXlsFile, naverXlsApi } from '@/api/entities/review/naverXls'

definePage({
  meta: {
    title: 'Naver XLS 임포트',
    action: 'read',
    subject: 'review',
  },
})

const { snackbar, handleError } = useErrorHandler()

const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 상태
const loading = ref(false)
const uploading = ref(false)
const uploadProgress = ref(0)
const driverConfigs = ref<DriverConfig[]>([])
const files = ref<NaverXlsFile[]>([])
const totalFiles = ref(0)

// 폼 상태
const selectedConfigId = ref<number | null>(null)
const selectedFile = ref<File | null>(null)
const fileInputKey = ref(0)

// 페이지네이션
const currentPage = ref(1)
const perPage = ref(15)
const lastPage = ref(1)

// 확인 다이얼로그
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => Promise<void>) | null>(null)

// 테이블 헤더
const headers = [
  { title: '파일명', key: 'file_name', sortable: false },
  { title: '드라이버', key: 'driver_config', sortable: false },
  { title: '업로드일', key: 'created_at', sortable: false },
  { title: '임포트일', key: 'imported_at', sortable: false },
  { title: '임포트 결과', key: 'import_result', sortable: false },
  { title: '작업', key: 'actions', sortable: false, width: '160px' },
]

// 드라이버 설정 로드
const loadDriverConfigs = async () => {
  if (!selectedClient.value)
    return

  try {
    const { success, data } = await driverConfigsApi.listDriverConfigs(selectedClient.value.id, {
      per_page: 100,
    } as unknown as DriverConfigsParams)

    if (success && data) {
      driverConfigs.value = (Array.isArray(data) ? data : (data as { data: DriverConfig[] }).data || [])
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
}

// 파일 목록 로드
const loadFiles = async () => {
  loading.value = true
  try {
    const configId = selectedConfigId.value ?? undefined
    const { success, data } = await naverXlsApi.list(configId, currentPage.value)

    if (success && data) {
      files.value = data.data
      totalFiles.value = data.total
      lastPage.value = data.last_page
      currentPage.value = data.current_page
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

// 파일 선택 핸들러
const onFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement

  selectedFile.value = input.files?.[0] ?? null
}

// 업로드
const handleUpload = async () => {
  if (!selectedFile.value || !selectedConfigId.value) {
    snackbar.warning('드라이버 설정과 파일을 선택해주세요.')
    
    return
  }

  uploading.value = true
  uploadProgress.value = 0

  try {
    const { success, error } = await naverXlsApi.upload(
      selectedFile.value,
      selectedConfigId.value,
      progress => {
        uploadProgress.value = progress
      },
    )

    if (success) {
      snackbar.success('파일이 업로드되었습니다.')
      selectedFile.value = null
      fileInputKey.value++
      await loadFiles()
    }
    else {
      snackbar.error(error?.message || '업로드 중 오류가 발생했습니다.')
    }
  }
  catch (error) {
    handleError(error)
  }
  finally {
    uploading.value = false
    uploadProgress.value = 0
  }
}

// 확인 다이얼로그 표시
const showConfirm = (message: string, action: () => Promise<void>) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

// 확인 실행
const executeConfirm = async () => {
  if (confirmAction.value)
    await confirmAction.value()

  confirmDialog.value = false
}

// 임포트 실행
const handleImport = (file: NaverXlsFile) => {
  const name = file.file_name || `파일 #${file.id}`

  showConfirm(
    `"${name}"을(를) ECS로 임포트하시겠습니까?`,
    async () => {
      const { success, error } = await naverXlsApi.import(file.id)

      if (success) {
        snackbar.success('임포트 작업이 시작되었습니다.')
        await loadFiles()
      }
      else {
        snackbar.error(error?.message || '임포트 요청 중 오류가 발생했습니다.')
      }
    },
  )
}

// 삭제
const handleDelete = (file: NaverXlsFile) => {
  const name = file.file_name || `파일 #${file.id}`

  showConfirm(
    `"${name}"을(를) 삭제하시겠습니까? S3에서도 삭제됩니다.`,
    async () => {
      const { success, error } = await naverXlsApi.remove(file.id)

      if (success) {
        snackbar.success('파일이 삭제되었습니다.')
        await loadFiles()
      }
      else {
        snackbar.error(error?.message || '삭제 중 오류가 발생했습니다.')
      }
    },
  )
}

// 임포트 결과 텍스트
const importStatusText = (file: NaverXlsFile): string => {
  if (!file.import_result)
    return '-'

  const result = file.import_result

  if (result.error)
    return `오류: ${result.error}`

  if (result.status)
    return String(result.status)

  return JSON.stringify(result)
}

// 임포트 결과 색상
const importStatusColor = (file: NaverXlsFile): string => {
  if (!file.import_result)
    return 'default'

  if (file.import_result.error)
    return 'error'

  return 'success'
}

// 날짜 포맷
const formatDate = (dateStr: string | null): string => {
  if (!dateStr)
    return '-'

  return new Date(dateStr).toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// 드라이버 이름
const getDriverName = (file: NaverXlsFile): string => {
  if (file.driver_config?.name)
    return file.driver_config.name

  return `설정 #${file.config_id}`
}

// 페이지 변경
const onPageChange = (page: number) => {
  currentPage.value = page
  loadFiles()
}

// 감시자
watch(selectedConfigId, () => {
  currentPage.value = 1
  loadFiles()
})

watch(selectedClient, () => {
  selectedConfigId.value = null
  driverConfigs.value = []
  files.value = []
  totalFiles.value = 0

  if (selectedClient.value)
    loadDriverConfigs()
})

onMounted(() => {
  if (selectedClient.value)
    loadDriverConfigs()

  loadFiles()
})
</script>

<template>
  <div>
    <!-- 고객사 선택 -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        md="4"
      >
        <ClientSelector />
      </VCol>
    </VRow>

    <!-- 업로드 폼 -->
    <VCard class="mb-4">
      <VCardTitle>Naver XLS 파일 업로드</VCardTitle>
      <VCardText>
        <VRow align="center">
          <VCol
            cols="12"
            md="3"
          >
            <VSelect
              v-model="selectedConfigId"
              :items="driverConfigs"
              item-title="name"
              item-value="id"
              label="드라이버 설정 선택"
              :disabled="!selectedClient || driverConfigs.length === 0"
              clearable
              hide-details
            />
          </VCol>
          <VCol
            cols="12"
            md="5"
          >
            <VFileInput
              :key="fileInputKey"
              accept=".xls,.xlsx"
              label="XLS/XLSX 파일 선택"
              :disabled="!selectedConfigId"
              hide-details
              @change="onFileChange"
            />
          </VCol>
          <VCol
            cols="12"
            md="2"
          >
            <VBtn
              color="primary"
              :disabled="!selectedFile || !selectedConfigId || uploading"
              :loading="uploading"
              block
              @click="handleUpload"
            >
              업로드
            </VBtn>
          </VCol>
        </VRow>

        <!-- 업로드 진행률 -->
        <VRow
          v-if="uploading && uploadProgress > 0"
          class="mt-2"
        >
          <VCol>
            <VProgressLinear
              :model-value="uploadProgress"
              color="primary"
              height="8"
              rounded
            >
              <template #default>
                {{ uploadProgress }}%
              </template>
            </VProgressLinear>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- 파일 목록 -->
    <VCard>
      <VCardTitle>업로드된 파일 목록</VCardTitle>
      <VCardText>
        <VDataTable
          :headers="headers"
          :items="files"
          :loading="loading"
          :items-per-page="perPage"
          hide-default-footer
          class="elevation-0"
        >
          <template #item.file_name="{ item }">
            {{ item.file_name || '-' }}
          </template>

          <template #item.driver_config="{ item }">
            {{ getDriverName(item) }}
          </template>

          <template #item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template #item.imported_at="{ item }">
            {{ formatDate(item.imported_at) }}
          </template>

          <template #item.import_result="{ item }">
            <VChip
              :color="importStatusColor(item)"
              size="small"
              variant="tonal"
            >
              {{ importStatusText(item) }}
            </VChip>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex gap-2">
              <VBtn
                color="primary"
                size="small"
                :disabled="!!item.imported_at"
                @click="handleImport(item)"
              >
                임포트
              </VBtn>
              <VBtn
                color="error"
                size="small"
                variant="outlined"
                @click="handleDelete(item)"
              >
                삭제
              </VBtn>
            </div>
          </template>

          <template #no-data>
            <div class="text-center py-8">
              업로드된 파일이 없습니다.
            </div>
          </template>
        </VDataTable>

        <!-- 페이지네이션 -->
        <div
          v-if="lastPage > 1"
          class="d-flex justify-center mt-4"
        >
          <VPagination
            :model-value="currentPage"
            :length="lastPage"
            @update:model-value="onPageChange"
          />
        </div>
      </VCardText>
    </VCard>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>확인</VCardTitle>
        <VCardText>{{ confirmMessage }}</VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="executeConfirm"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </div>
</template>
